// Work kit — issue FIELD controller (wave 4, Track B/C): the F4 mutation seam
// for one editable field.
//
// Mounted per field (details rows, the header summary, the watch toggle).
// Owns the pinned semantics:
//   - inline-swap editors (input/textarea/summary) commit on Enter and on
//     focusout; select-family editors commit on their `change` /
//     `ui--date-picker:change` / `ui--combo-box:change` signals,
//   - Esc cancels with NO event emitted and the pre-edit display restored
//     verbatim (pin C4),
//   - F4 seam: snapshot → optimistic apply → emit ui--work:field-update →
//     host POSTs → host dispatches ui--work:field-ack {issue_id, field_key,
//     ok, value?} → ok adopts server truth; !ok reverts + exactly ONE toast
//     (the shared seam replaces, never stacks),
//   - single-flight coalescing per (issue_id, field_key): a change made while
//     a mutation is in flight persists after the first ack settles (pin C6),
//   - stale-rollback guard: if a later gesture mutated the field when the
//     failing ack arrives, the revert is skipped (the queued edit re-applies
//     instead — no stale flash).
//
// Event emission + toasts go through the shared seam
// (app/javascript/lib/work/mutation_seam.js) — never a new display path.

import { Controller } from "@hotwired/stimulus"
import { adoptOrRollback, emitFromIssueRoot, showToast, toastRegionFor } from "../../../lib/work/mutation_seam"

const FIELD_ACK_EVENT = "ui--work:field-ack"
const FIELD_UPDATE_EVENT = "ui--work:field-update"

export default class extends Controller {
  static values = {
    issueId: String,
    fieldKey: String,
    fieldType: String,
    value: String,
    disabled: { type: Boolean, default: false },
    loadingToast: { type: Boolean, default: false },
    placeholder: { type: String, default: "" },
    errorMessage: { type: String, default: "" },
  }

  static targets = ["display", "editor", "displayText", "input", "live", "toggle"]

  connect() {
    this._teardown()
    this._pending = null // value of the in-flight mutation (single-flight gate)
    this._queued = null // value typed while one is in flight (coalesced)
    this._snapshot = null // { value, text, watching } — pre-edit state
    this._editing = false

    this._onAck = this._handleAck.bind(this)
    document.addEventListener(FIELD_ACK_EVENT, this._onAck)
  }

  disconnect() {
    this._teardown()
    this._teardownEsc()
  }

  // Teardown-first re-bind (dashboard_grid idiom, ghost-closure family): a
  // same-instance double-connect (Turbo cache restore racing a morph) must
  // not stack a second document ack closure — the orphaned ghost re-handles
  // acks (double toasts, double adopt/revert). Removing first is a no-op
  // when nothing is bound. The connect-time state reset below is this
  // controller's own lifecycle (unchanged) — only the listener hygiene is
  // teardown-first.
  _teardown() {
    document.removeEventListener(FIELD_ACK_EVENT, this._onAck)
    this._onAck = null
  }

  // --- Public data API (spec Track G deliverable 2) -------------------------
  //
  // Hosts drive the field lifecycle programmatically. These are the same
  // code paths the ack seam uses — NOT separate mutation paths — documented
  // in docs/work/javascript-api.md. Both no-op while a mutation is in
  // flight: the LWW gate (_pending) owns the field then, and a host-driven
  // adopt/revert inside that window would corrupt the adoptOrRollback
  // contract (the seam converges the field itself when the ack lands).

  // Converge the field to an authoritative server value without a mutation
  // round-trip (host refetch, broadcast update). `ack` is the same shape the
  // ui--work:field-ack seam carries: { value } (value optional).
  adoptField(ack = {}) {
    if (this._pending) return
    this._adopt(ack)
  }

  // Restore the field to its pre-edit snapshot and close the editor —
  // host-driven cancel (e.g. a parent drawer closing underneath the edit).
  // No-op when nothing is editing.
  revertField() {
    if (this._pending) return
    if (!this._editing) return
    this._revert()
  }

  // --- inline-swap editors (input / textarea / summary) ---

  edit() {
    if (this.disabledValue || this._editing) return
    this._editing = true
    // A pending mutation owns the baseline snapshot: re-capturing here would
    // snapshot the OPTIMISTIC display, and a failing ack would then "revert"
    // to the value that is about to roll back. Keep the pending baseline.
    if (this._pending === null) this._snapshot = this._capture()
    this.element.setAttribute("data-work-editing", "true")
    // Layered Esc (pin A4/D3): while an editor is open, a document-level
    // CAPTURE handler cancels the editor first and vetoes the drawer/modal
    // Esc — the drawer must not close underneath the edit.
    this._escHandler = (event) => {
      if (event.key !== "Escape") return
      event.preventDefault()
      event.stopPropagation()
      this.cancel()
    }
    document.addEventListener("keydown", this._escHandler, true)

    this.displayTarget.hidden = true
    this.editorTarget.hidden = false
    const input = this._inputElement()
    if (input) {
      input.value = this.valueValue
      input.focus()
      if (input.select) input.select()
    }
  }

  // Keydown on the editor wrapper: Enter commits (Shift+Enter keeps its
  // native newline in the textarea), Esc is normally intercepted by the
  // capture veto first — this is the bubble-phase fallback.
  keydown(event) {
    if (event.key !== "Enter" || event.shiftKey) return
    event.preventDefault()
    event.stopPropagation()
    this.commit()
  }

  // focusout bubbles; blur does not. Commit when focus leaves the editor.
  commit() {
    if (!this._editing) return
    this._teardownEsc()
    this.element.removeAttribute("data-work-editing")
    const input = this._inputElement()
    const next = input ? input.value : this.valueValue
    // Optimistic close: the display returns immediately (the optimistic text
    // renders below); the ack then adopts or reverts (pin C5).
    this._editing = false
    if (this.hasEditorTarget) this.editorTarget.hidden = true
    if (this.hasDisplayTarget) this.displayTarget.hidden = false
    if (next === this.valueValue) return
    this._apply(next)
  }

  cancel() {
    if (!this._editing) return
    this._editing = false
    this._teardownEsc()
    this.element.removeAttribute("data-work-editing")
    // Byte-identical restore: the display's pre-edit text and the controller's
    // pre-edit value come back unchanged; NO event, NO toast (pin C4).
    if (this._snapshot) this._restoreFromSnapshot()
    if (this.hasEditorTarget) this.editorTarget.hidden = true
    if (this.hasDisplayTarget) this.displayTarget.hidden = false
    this._snapshot = null
  }

  // Commit path for select-family editors: a `change` bubbling from a native
  // input/checkbox/hidden input, or the `ui--date-picker:change` /
  // `ui--combo-box:change` custom events (detail.value). Duplicate signals
  // (combo fires both) dedupe on the unchanged-value guard.
  editorChanged(event) {
    if (this.disabledValue) return
    const next = this._readEditorValue(event)
    if (next === null) return
    if (next === this.valueValue) return
    this._apply(next)
  }

  // Toggle path (watching). One field-update per press (pin B6).
  toggleWatch() {
    if (this.disabledValue) return
    this._apply(this.valueValue === "true" ? "false" : "true")
  }

  // --- internals ---

  _apply(next) {
    // Single-flight coalescing (pin C6): a change while one is in flight is
    // held; it persists (re-applies) after the in-flight ack settles.
    if (this._pending !== null) {
      this._queued = next
      return
    }

    if (!this._snapshot) this._snapshot = this._capture()
    this._pending = next

    // Optimistic apply: the controller value and the visible display update
    // immediately; server truth lands via the ack.
    this.valueValue = next
    this._renderDisplay(next)
    if (this.hasToggleTarget) this._renderToggle(next === "true")
    if (this.loadingToastValue) {
      showToast(toastRegionFor(this.element), "", "loading")
    }

    emitFromIssueRoot(this.element, FIELD_UPDATE_EVENT, {
      issue_id: this.issueIdValue,
      field_key: this.fieldKeyValue,
      value: next,
    })
  }

  // The ack routes through the shared seam: `ok` adopts the response-carried
  // value (server truth wins); `!ok` reverts + exactly one toast (two rapid
  // error acks cannot stack two nodes — the seam replaces the current one).
  _handleAck(event) {
    const detail = event.detail || {}
    if (detail.field_key !== this.fieldKeyValue) return
    if (detail.issue_id !== this.issueIdValue) return

    const region = toastRegionFor(this.element)
    const outcome = adoptOrRollback({
      ack: detail,
      region,
      applyAdopt: (ack) => this._adopt(ack),
      applyRevert: () => this._revert(),
      errorMessage: this.errorMessageValue,
    })

    if (outcome === "adopted") {
      this._pending = null
      if (this.hasLiveTarget) this.liveTarget.textContent = ""
      // Coalesced edit: persist what the user changed while the first
      // mutation was in flight (pin C6).
      if (this._queued !== null && this._queued !== this.valueValue) {
        const queued = this._queued
        this._queued = null
        this._apply(queued)
      } else {
        this._queued = null
        this._snapshot = null
      }
    } else if (outcome === "reverted") {
      this._pending = null
      if (this._queued !== null && this._queued !== this._snapshotValue()) {
        // Stale-rollback guard: a later gesture mutated this field after the
        // failing mutation was sent — skip the revert flash, re-apply the
        // newer edit instead (the host refetches on its own schedule).
        const queued = this._queued
        this._queued = null
        this._apply(queued)
      } else {
        // adoptOrRollback already ran applyRevert (this._revert) before the
        // toast — restore happens exactly once (fresh-eyes fix: the previous
        // code reverted twice here).
        this._queued = null
        if (this.hasLiveTarget) this.liveTarget.textContent = this.errorMessageValue
        this._snapshot = null
      }
    }
  }

  _adopt(ack) {
    if (ack.value !== undefined && ack.value !== null) {
      this.valueValue = String(ack.value)
      this._renderDisplay(String(ack.value))
      if (this.hasToggleTarget) this._renderToggle(String(ack.value) === "true")
    }
    if (this.hasEditorTarget) this.editorTarget.hidden = true
    if (this.hasDisplayTarget) this.displayTarget.hidden = false
    this._editing = false
  }

  _revert() {
    this._restoreFromSnapshot()
    if (this.hasEditorTarget) this.editorTarget.hidden = true
    if (this.hasDisplayTarget) this.displayTarget.hidden = false
    this._editing = false
  }

  _capture() {
    return {
      value: this.valueValue,
      text: this._currentDisplayText(),
      watching: this.hasToggleTarget ? this.toggleTarget.getAttribute("aria-pressed") === "true" : null,
    }
  }

  _snapshotValue() {
    return this._snapshot ? this._snapshot.value : this.valueValue
  }

  _restoreFromSnapshot() {
    if (!this._snapshot) return
    this.valueValue = this._snapshot.value
    if (this._snapshot.text !== null) this._renderDisplay(this._snapshot.text, { raw: true })
    if (this._snapshot.watching !== null && this.hasToggleTarget) {
      this._renderToggle(this._snapshot.watching)
    }
  }

  _currentDisplayText() {
    if (this.hasDisplayTextTarget) return this.displayTextTarget.textContent
    if (this.hasDisplayTarget) {
      const button = this.displayTarget.querySelector("button")
      if (button) return button.textContent
    }
    return null
  }

  _renderDisplay(text, { raw = false } = {}) {
    const shown = raw ? text : text === "" ? (this.hasDisplayTextTarget ? "—" : "") : text
    if (this.hasDisplayTextTarget) {
      this.displayTextTarget.textContent = shown
    } else if (this.hasDisplayTarget) {
      const button = this.displayTarget.querySelector("button")
      if (button) button.textContent = shown
    }
  }

  _renderToggle(watching) {
    const button = this.toggleTarget
    button.setAttribute("aria-pressed", String(watching))
    button.classList.toggle("text-primary", watching)
    button.classList.toggle("text-foreground-medium", !watching)
    button.classList.toggle("hover:text-foreground", !watching)
    button.querySelectorAll("[data-work-toggle-icon]").forEach((span) => {
      const isOnIcon = span.getAttribute("data-work-toggle-icon") === "on"
      span.hidden = isOnIcon !== watching
    })
  }

  _teardownEsc() {
    if (this._escHandler) {
      document.removeEventListener("keydown", this._escHandler, true)
      this._escHandler = null
    }
  }

  _inputElement() {
    if (!this.hasInputTarget) return null
    const root = this.inputTarget.matches("input, textarea") ? this.inputTarget : this.inputTarget.querySelector("input, textarea")
    return root
  }

  // Read the committed value from any editor signal. Checkbox groups join
  // their checked values as a JSON array (matching the injected value shape);
  // custom editor events carry detail.value; everything else reads the
  // changed element.
  _readEditorValue(event) {
    if (event.detail && typeof event.detail.value !== "undefined") {
      return event.detail.value === null ? null : String(event.detail.value)
    }
    const target = event.target
    if (target instanceof HTMLInputElement && target.type === "checkbox") {
      const checked = Array.from(this.element.querySelectorAll("input[type=checkbox]:checked")).map((el) => el.value)
      return JSON.stringify(checked)
    }
    if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
      return target.value
    }
    return null
  }
}
